import {
  ADMIN_PERMISSIONS,
  archiveHoldAfterRestore,
  maskPhone,
  REGISTRANT_TYPE_LABELS,
  validateChangeRequest,
  validateJobInput,
  validateRegistration,
  type AccessRotateTarget,
  type ActivityAction,
  type ActivityLogDoc,
  type AddRegistrationRequest,
  type ChangeRequestDoc,
  type ContractorDoc,
  type JobDoc,
  type JobUpdateRequest,
  type JobWriteRequest,
  type MeResponse,
  type NotificationDoc,
  type NotificationType,
  type PersonalRequestView,
  type PersonalViewResponse,
  type PublicJobInfo,
  type RegisterRequest,
  type RegistrantType,
  type RegistrationAction,
  type RegistrationDoc,
  type RequestDecision,
  type TokenAction,
  announcementVisibleTo,
  threadIdFor,
  validateAnnouncement,
  validateMessageText,
  type AnnouncementDoc,
  type AnnouncementRequest,
  type ChatResponse,
  type MessageDoc,
  type PublicAnnouncement,
  type TeamMessageDoc,
  type ThreadDoc,
  formatPhoneForDisplay,
  parseIsraeliPhone,
  PHONE_ERROR_MESSAGES,
  type UpdateContractorRequest,
  resolveSettings,
  sanitizeErrorReport,
  validateSettingsPatch,
  type AddAdminRequest,
  type AddAdminResponse,
  type AdminActionRequest,
  type AdminListItem,
  type AdminsResponse,
  type ErrorLogItem,
  type ErrorReportRequest,
  type OwnerHealthResponse,
  type SettingsDoc,
  type SettingsResponse,
  type SystemSettings,
} from '@crew/shared';
import { addDays, todayInIsrael } from '@/lib/format';
import { dateToTs, tsToDate, type Ts } from '@/lib/ts';
import { ApiClientError } from '@/services/apiError';
import { DEMO_CREDENTIALS, IS_DEMO } from './demoMode';

const STORAGE_KEY = 'sa-demo-state-v2';

/**
 * מאגר בזיכרון למצב התצוגה בלבד. מחקה את התנהגות השרת (בדיקות, כפילויות, בקשות, התראות, יומן)
 * כדי שאפשר יהיה לנסות את כל המסכים בלי Firebase. מתאפס ברענון הדף.
 */

export const DEMO_ME: MeResponse = {
  uid: 'demo-admin',
  email: DEMO_CREDENTIALS.email,
  displayName: 'מנהל לדוגמה',
  permissions: ADMIN_PERMISSIONS,
  // במצב תצוגה המשתמש הוא המפתח, כדי שאפשר יהיה לראות את מסך המפתח
  role: 'owner',
  quietMode: false,
};

interface DemoAdmin {
  uid: string;
  email: string;
  displayName: string;
  active: boolean;
  removed: boolean;
  hasPassword: boolean;
  createdAt: string;
}

export type DemoJob = JobDoc<Ts> & { id: string };
export type DemoReg = RegistrationDoc<Ts> & { id: string };
export type DemoLog = ActivityLogDoc<Ts> & { id: string };
export type DemoRequest = ChangeRequestDoc<Ts> & { id: string };
export type DemoContractor = ContractorDoc<Ts> & { id: string };
export type DemoNotification = NotificationDoc<Ts> & { id: string };
export type DemoAnnouncement = AnnouncementDoc<Ts> & { id: string };
export type DemoThread = ThreadDoc<Ts> & { id: string };
export type DemoMessage = MessageDoc<Ts> & { id: string };
export type DemoTeamMessage = TeamMessageDoc<Ts> & { id: string };

/** מנהלים נוספים לדוגמה — כדי שהצ'אט הפנימי ייראה חי */
const OTHER_ADMINS = [
  { uid: 'demo-admin-2', displayName: 'דנה כהן' },
  { uid: 'demo-admin-3', displayName: 'יוסי לוי' },
];

interface Seed {
  name: string;
  dayOffset: number;
  reportTime: string;
  location: string;
  contractors: Array<[string, string, number]>;
  workers: Array<[string, string]>;
  open: boolean;
  notes?: string;
  /** [אינדקס נרשם, כמות מבוקשת, הערה] */
  requests?: Array<[number, number, string]>;
}

const SEEDS: Seed[] = [
  {
    name: 'אבטחת אירוע — היכל התרבות',
    dayOffset: 0,
    reportTime: '18:30',
    location: 'כיכר הבימה 1, תל אביב',
    contractors: [
      ['משה כהן', '0501234567', 6],
      ['אבי לוי', '0521112233', 4],
      ['יוסי מזרחי', '0543334455', 8],
      ['דוד פרץ', '0527778899', 3],
      ['רונן ביטון', '0509990011', 5],
    ],
    workers: [
      ['עומר אברהם', '0548887766'],
      ['שי דהן', '0586665544'],
      ['איתי אזולאי', '0532223344'],
      ['נועם גבאי', '0524445566'],
    ],
    open: false,
    notes: 'להגיע עם אפוד ותעודה מזהה. תדריך בכניסה הראשית.',
    requests: [[0, 8, 'הצטרפו עוד שני עובדים']],
  },
  {
    name: 'שמירה באתר בנייה — מגדלי הים',
    dayOffset: 0,
    reportTime: '06:00',
    location: 'שדרות בן גוריון 40, בת ים',
    contractors: [
      ['אליהו שושן', '0502223344', 5],
      ['גיל חדד', '0546667788', 3],
    ],
    workers: [['ליאור בן חיים', '0523339911']],
    open: false,
  },
  {
    name: 'אבטחת כנס — גני התערוכה',
    dayOffset: 1,
    reportTime: '07:15',
    location: 'רוקח 101, תל אביב',
    contractors: [
      ['ששון אלמוג', '0507771122', 7],
      ['מאיר עמר', '0549993344', 6],
      ['ניר אוחיון', '0525556677', 4],
      ['חיים טל', '0531114455', 3],
    ],
    workers: [
      ['תומר ריבלין', '0581237777'],
      ['עדי כץ', '0547654321'],
      ['רועי נחום', '0528889900'],
      ['יובל מור', '0501112299'],
      ['בר אשכנזי', '0533332211'],
      ['אור סעדה', '0544441122'],
    ],
    open: true,
    requests: [
      [1, 4, 'שניים חולים'],
      [5, 0, 'לא אוכל להגיע'],
    ],
  },
  {
    name: 'סיור לילה — פארק תעשייה',
    dayOffset: 3,
    reportTime: '22:00',
    location: 'אזור התעשייה, קיסריה',
    contractors: [['ברק יוסף', '0508765432', 4]],
    workers: [
      ['עידן לוי', '0547771100'],
      ['שחר דוד', '0526660099'],
    ],
    open: true,
  },
  {
    name: 'אבטחת חתונה — אולמי הגן',
    dayOffset: 5,
    reportTime: '17:00',
    location: 'דרך השרון 12, רעננה',
    contractors: [
      ['משה כהן', '0501234567', 5],
      ['אבי לוי', '0521112233', 3],
    ],
    workers: [
      ['עומר אברהם', '0548887766'],
      ['שי דהן', '0586665544'],
      ['נועם גבאי', '0524445566'],
    ],
    open: true,
  },
  { name: 'מאבטחים לקניון — סוף שבוע', dayOffset: 11, reportTime: '09:00', location: 'קניון הזהב, ראשון לציון', contractors: [], workers: [], open: true },
  {
    name: 'אבטחת הופעה — פארק הירקון',
    dayOffset: -4,
    reportTime: '19:00',
    location: 'פארק הירקון, תל אביב',
    contractors: [
      ['ששון אלמוג', '0507771122', 12],
      ['משה כהן', '0501234567', 9],
    ],
    workers: [['עדי כץ', '0547654321']],
    open: false,
  },
];

const e164 = (local: string) => `+972${local.replace(/\D/g, '').replace(/^0/, '')}`;
const cidOf = (phoneE164: string) => `d${phoneE164.replace(/\D/g, '')}`;
let seq = 0;
const nextId = (prefix: string) => `${prefix}${(++seq).toString(36).padStart(5, '0')}${Math.random().toString(36).slice(2, 8)}`;
const randomToken = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let t = '';
  for (let i = 0; i < 43; i++) t += chars[Math.floor(Math.random() * chars.length)];
  return t;
};
const fail = (code: ConstructorParameters<typeof ApiClientError>[0], message: string, status = 400, fields?: Record<string, string>) =>
  new ApiClientError(code, message, status, undefined, fields);
const delay = () => new Promise((r) => setTimeout(r, 250));

export class DemoStore {
  private jobsById = new Map<string, DemoJob>();
  private secrets = new Map<string, { accessCode: string; rotatedAt: Ts }>();
  private regs = new Map<string, DemoReg[]>();
  private contractorsById = new Map<string, DemoContractor>();
  private tokens = new Map<string, { jobId: string; cid: string; revoked: boolean }>();
  private reqs: DemoRequest[] = [];
  private anns = new Map<string, DemoAnnouncement[]>();
  private threadsById = new Map<string, DemoThread>();
  private msgs = new Map<string, DemoMessage[]>();
  private team: DemoTeamMessage[] = [];
  private teamReadAt: Ts | null = null;
  /** טלפון → מזהה, אחרי עריכת מספר (כמו phoneIds בשרת) */
  private phoneIds = new Map<string, string>();
  // ── מסך המפתח ──
  private admins: DemoAdmin[] = [];
  private settingsDoc: SettingsDoc<Ts> = {};
  private errors: ErrorLogItem[] = [];
  private quiet = false;
  private notes: DemoNotification[] = [];
  private logs: DemoLog[] = [];
  private listeners = new Set<() => void>();
  private seeded = false;

  constructor() {
    // סנכרון בין לשוניות: שינוי בלשונית אחת (למשל פתיחת הרשמה) מופיע מיד בשנייה
    if (typeof window !== 'undefined' && IS_DEMO) {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY) return;
        if (e.newValue === null) this.clear();
        else this.restore();
        this.notifyListeners();
      });
    }
  }

  // ── שמירה בדפדפן ──
  // מצב התצוגה נשמר ב-localStorage, כדי שכל הלשוניות (מנהל, קישור הרשמה, דף אישי)
  // יראו את אותם נתונים, ושהם יישארו אחרי רענון. "איפוס" מחזיר לנתוני הדוגמה.

  private persist() {
    try {
      const snapshot = {
        v: 2,
        anns: [...this.anns],
        threads: [...this.threadsById],
        msgs: [...this.msgs],
        team: this.team,
        teamReadAt: this.teamReadAt,
        phoneIds: [...this.phoneIds],
        admins: this.admins,
        settingsDoc: this.settingsDoc,
        errors: this.errors.slice(0, 200),
        quiet: this.quiet,
        jobs: [...this.jobsById],
        secrets: [...this.secrets],
        regs: [...this.regs],
        contractors: [...this.contractorsById],
        tokens: [...this.tokens],
        reqs: this.reqs,
        notes: this.notes,
        logs: this.logs.slice(0, 300),
        sampleTokens: [...this.sampleTokens],
      };
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(snapshot, (_k, v: unknown) =>
          v && typeof v === 'object' && typeof (v as Ts).toDate === 'function' ? { __ts: (v as Ts).toDate().toISOString() } : v,
        ),
      );
    } catch {
      /* אחסון חסום (גלישה פרטית וכו') — מצב התצוגה עובד בזיכרון בלבד */
    }
  }

  private restore(): boolean {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const s = JSON.parse(raw, (_k, v: unknown) =>
        v && typeof v === 'object' && '__ts' in (v as Record<string, unknown>) ? dateToTs(new Date((v as { __ts: string }).__ts)) : v,
      );
      if (s?.v !== 2) return false;
      this.anns = new Map(s.anns);
      this.threadsById = new Map(s.threads);
      this.msgs = new Map(s.msgs);
      this.team = s.team;
      this.teamReadAt = s.teamReadAt;
      this.phoneIds = new Map(s.phoneIds ?? []);
      this.admins = s.admins ?? [];
      this.settingsDoc = s.settingsDoc ?? {};
      this.errors = s.errors ?? [];
      this.quiet = s.quiet === true;
      this.jobsById = new Map(s.jobs);
      this.secrets = new Map(s.secrets);
      this.regs = new Map(s.regs);
      this.contractorsById = new Map(s.contractors);
      this.tokens = new Map(s.tokens);
      this.reqs = s.reqs;
      this.notes = s.notes;
      this.logs = s.logs;
      this.sampleTokens.clear();
      for (const [k, v] of s.sampleTokens as Array<[string, string]>) this.sampleTokens.set(k, v);
      return true;
    } catch {
      return false;
    }
  }

  private clear() {
    this.jobsById.clear();
    this.secrets.clear();
    this.regs.clear();
    this.contractorsById.clear();
    this.tokens.clear();
    this.sampleTokens.clear();
    this.reqs = [];
    this.anns.clear();
    this.threadsById.clear();
    this.msgs.clear();
    this.team = [];
    this.teamReadAt = null;
    this.phoneIds.clear();
    this.admins = [];
    this.settingsDoc = {};
    this.errors = [];
    this.quiet = false;
    this.notes = [];
    this.logs = [];
    this.seeded = false;
  }

  /** חזרה לנתוני הדוגמה המקוריים (בכל הלשוניות). */
  reset() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.clear();
    this.ensureSeed();
    this.notifyListeners();
  }

  // ── זריעה ──
  private ensureSeed() {
    if (this.seeded) return;
    this.seeded = true;
    if (this.restore()) return;
    this.seed();
    this.persist();
  }

  private seed() {
    const today = todayInIsrael();
    const t0 = Date.now();
    SEEDS.forEach((s, i) => {
      const id = `demojob${i + 1}`.padEnd(20, '0');
      const created = new Date(t0 - (SEEDS.length - i) * 3_600_000);
      const archived = s.dayOffset < 0;
      this.jobsById.set(id, {
        id,
        name: s.name,
        date: addDays(today, s.dayOffset),
        reportTime: s.reportTime,
        location: s.location,
        notes: s.notes ?? '',
        status: archived ? 'archived' : 'active',
        registration: s.open ? 'open' : 'closed',
        registrationClosedReason: s.open ? null : 'manual',
        registrationClosesAt: null,
        registrationSlug: `demo${i + 1}abcdefgh`.slice(0, 10),
        totalWorkers: 0,
        contractorCount: 0,
        individualWorkerCount: 0,
        pendingRequestCount: 0,
        archivedAt: archived ? dateToTs(created) : null,
        archiveHoldUntil: null,
        createdBy: DEMO_ME.uid,
        updatedBy: DEMO_ME.uid,
        createdAt: dateToTs(created),
        updatedAt: dateToTs(created),
        version: 1,
      });
      this.secrets.set(id, { accessCode: String(100000 + ((i * 271_829) % 900_000)), rotatedAt: dateToTs(created) });
      this.regs.set(id, []);
      this.log(DEMO_ME, 'job.create', id, null, { name: s.name }, created);
      const people: Array<[RegistrantType, string, string, number]> = [
        ...s.contractors.map(([n, p, c]) => ['contractor', n, p, c] as [RegistrantType, string, string, number]),
        ...s.workers.map(([n, p]) => ['worker', n, p, 1] as [RegistrantType, string, string, number]),
      ];
      people.forEach(([type, name, phone, count], j) => {
        const at = new Date(created.getTime() + (j + 1) * 600_000);
        const { token } = this.insertReg(id, { registrantType: type, fullName: name, phoneE164: e164(phone), workerCount: count }, 'self', at, i === 2 && j >= people.length - 2);
        // הקישור האישי של הנרשם הראשון בכל עבודה — כדי שאפשר יהיה לנסות את המסך האישי
        if (j === 0) this.sampleTokens.set(id, token);
      });
      for (const [idx, requested, note] of s.requests ?? []) {
        const reg = this.regs.get(id)![idx]!;
        this.insertRequest(id, reg, requested, note, new Date(t0 - (5 - idx) * 900_000));
      }
      this.recompute(id);
    });
    // עדכונים, שיחה וצ'אט צוות לדוגמה
    const jobs = [...this.jobsById.values()];
    const conf = jobs[2]!;
    const mk = (text: string, audience: AnnouncementDoc['audience'], showOnRegistration: boolean, at: Date, by = OTHER_ADMINS[0]!): DemoAnnouncement => ({
      id: nextId('ann'),
      text,
      audience,
      recipientIds: [],
      showOnRegistration,
      createdBy: by.uid,
      createdByName: by.displayName,
      createdAt: dateToTs(at),
      updatedAt: dateToTs(at),
    });
    this.anns.set(conf.id, [
      mk('מחפשים עוד 5 עובדים לכנס. מי שיכול להביא — להירשם או לעדכן כמות.', 'all', true, new Date(t0 - 2 * 3_600_000)),
      mk('לקבלנים: לשלוח רשימת שמות של העובדים עד 17:00 היום.', 'contractors', false, new Date(t0 - 3_600_000), OTHER_ADMINS[1]),
    ]);
    const firstReg = this.regs.get(conf.id)![0]!;
    const tid = threadIdFor(conf.id, firstReg.id);
    const m1 = new Date(t0 - 50 * 60_000);
    const m2 = new Date(t0 - 40 * 60_000);
    const m3 = new Date(t0 - 12 * 60_000);
    this.msgs.set(tid, [
      { id: nextId('msg'), sender: 'contractor', authorId: `contractor:${firstReg.id}`, authorName: firstReg.fullName, text: 'יש חניה לרכבים של העובדים?', createdAt: dateToTs(m1) },
      { id: nextId('msg'), sender: 'admin', authorId: OTHER_ADMINS[0]!.uid, authorName: OTHER_ADMINS[0]!.displayName, text: 'כן, חניון B ליד שער 3.', createdAt: dateToTs(m2) },
      { id: nextId('msg'), sender: 'contractor', authorId: `contractor:${firstReg.id}`, authorName: firstReg.fullName, text: 'מעולה. אנחנו מגיעים עם 2 רכבים, נהיה שם ב-7:00.', createdAt: dateToTs(m3) },
    ]);
    this.threadsById.set(tid, {
      id: tid,
      jobId: conf.id,
      jobName: conf.name,
      jobDate: conf.date,
      contractorId: firstReg.id,
      contractorName: firstReg.fullName,
      registrantType: firstReg.registrantType,
      lastMessageAt: dateToTs(m3),
      lastMessageText: 'מעולה. אנחנו מגיעים עם 2 רכבים, נהיה שם ב-7:00.',
      lastSender: 'contractor',
      readBy: { [OTHER_ADMINS[0]!.uid]: dateToTs(m2) },
      contractorReadAt: dateToTs(m3),
      closed: false,
    });
    this.notify('message', conf.id, `הודעה מ${firstReg.fullName}`, `מעולה. אנחנו מגיעים עם 2 רכבים… — ${conf.name}`, `/messages/${tid}`, null, m3, `message:${tid}`);
    this.team = [
      { id: nextId('team'), authorUid: OTHER_ADMINS[0]!.uid, authorName: OTHER_ADMINS[0]!.displayName, text: 'מי לוקח את הכנס מחר? אני יכולה להיות שם מ-6:30.', createdAt: dateToTs(new Date(t0 - 90 * 60_000)) },
      { id: nextId('team'), authorUid: OTHER_ADMINS[1]!.uid, authorName: OTHER_ADMINS[1]!.displayName, text: 'אני אסגור את ההרשמה לחתונה ביום רביעי בערב.', createdAt: dateToTs(new Date(t0 - 30 * 60_000)) },
    ];
    this.notify('team_message', null, `${OTHER_ADMINS[1]!.displayName} בצ'אט המנהלים`, 'אני אסגור את ההרשמה לחתונה ביום רביעי בערב.', '/messages?tab=team', OTHER_ADMINS[1]!.uid, new Date(t0 - 30 * 60_000), `team_message:${OTHER_ADMINS[1]!.uid}`);

    // התראה על סגירת הרשמה לדוגמה
    const first = [...this.jobsById.values()][0]!;
    this.notify('registration_closed', first.id, 'ההרשמה נסגרה', `מנהל לדוגמה סגר/ה את ההרשמה ל"${first.name}".`, `/jobs/${first.id}`, null, new Date(t0 - 1_800_000));
  }

  /** קישור אישי לדוגמה לכל עבודה (רק במצב תצוגה) */
  readonly sampleTokens = new Map<string, string>();

  private recompute(jobId: string) {
    const j = this.jobsById.get(jobId);
    if (!j) return;
    const regs = this.regs.get(jobId) ?? [];
    this.jobsById.set(jobId, {
      ...j,
      totalWorkers: regs.reduce((n, r) => n + r.workerCount, 0),
      contractorCount: regs.filter((r) => r.registrantType === 'contractor').length,
      individualWorkerCount: regs.filter((r) => r.registrantType === 'worker').length,
      pendingRequestCount: this.reqs.filter((r) => r.jobId === jobId && r.status === 'pending').length,
    });
  }

  private log(actor: { uid: string; displayName: string }, action: ActivityAction, jobId: string | null, before: Record<string, unknown> | null, after: Record<string, unknown> | null, at = new Date()) {
    this.logs.unshift({
      id: nextId('log'),
      actorUid: actor.uid,
      actorName: actor.displayName,
      action,
      entityType: 'job',
      entityId: jobId ?? '',
      jobId,
      before,
      after,
      createdAt: dateToTs(at),
    });
  }

  private notify(type: NotificationType, jobId: string | null, title: string, body: string, link: string, actorUid: string | null, at = new Date(), id = nextId('ntf')) {
    this.notes = [{ id, type, jobId, title, body, link, actorUid, readBy: {}, resolution: null, resolvedByName: null, createdAt: dateToTs(at) }, ...this.notes.filter((n) => n.id !== id)];
  }

  private insertReg(
    jobId: string,
    r: { registrantType: RegistrantType; fullName: string; phoneE164: string; workerCount: number },
    source: 'self' | 'admin',
    at: Date,
    withNotice = true,
  ): { token: string; reg: DemoReg } {
    const cid = this.resolveCid(r.phoneE164);
    const regs = this.regs.get(jobId) ?? [];
    if (regs.some((x) => x.id === cid)) {
      throw fail('conflict', source === 'self' ? 'מספר הטלפון הזה כבר רשום לעבודה. לשינוי השתמשו בקישור האישי שקיבלתם בהרשמה, או פנו למנהל' : 'מספר הטלפון הזה כבר רשום לעבודה', 409);
    }
    const token = randomToken();
    this.tokens.set(token, { jobId, cid, revoked: false });
    const reg: DemoReg = {
      id: cid,
      contractorId: cid,
      registrantType: r.registrantType,
      fullName: r.fullName,
      phone: r.phoneE164,
      workerCount: r.workerCount,
      source,
      pendingRequestId: null,
      personalTokenHash: token,
      createdAt: dateToTs(at),
      updatedAt: dateToTs(at),
      version: 1,
    };
    this.regs.set(jobId, [...regs, reg]);
    const c = this.contractorsById.get(cid);
    const jobIds = [...new Set([...(c?.jobIds ?? []), jobId])];
    this.contractorsById.set(cid, {
      id: cid,
      fullName: c?.fullName ?? r.fullName,
      phone: r.phoneE164,
      lastRegistrantType: r.registrantType,
      jobIds,
      jobCount: jobIds.length,
      searchTokens: [],
      createdAt: c?.createdAt ?? dateToTs(at),
      updatedAt: dateToTs(at),
      lastSeenAt: dateToTs(at),
    });
    const job = this.jobsById.get(jobId)!;
    const actor = source === 'self' ? { uid: `contractor:${cid}`, displayName: r.fullName } : DEMO_ME;
    this.log(actor, source === 'self' ? 'registration.self' : 'registration.add', jobId, null, { fullName: r.fullName, registrantType: r.registrantType, workerCount: r.workerCount }, at);
    if (withNotice) {
      const what = r.registrantType === 'contractor' ? `קבלן עם ${r.workerCount} עובדים` : 'עובד עצמאי';
      this.notify('registration', jobId, source === 'self' ? 'נרשם חדש' : 'נרשם נוסף ידנית', `${r.fullName} (${what}) — ${job.name}`, `/jobs/${jobId}`, source === 'admin' ? DEMO_ME.uid : null, at, `registration:${jobId}:${cid}`);
    }
    this.recompute(jobId);
    return { token, reg };
  }

  private insertRequest(jobId: string, reg: DemoReg, requestedCount: number, note: string, at = new Date()): string {
    const job = this.jobsById.get(jobId)!;
    const id = nextId('req');
    this.reqs.unshift({
      id,
      jobId,
      jobName: job.name,
      jobDate: job.date,
      contractorId: reg.id,
      registrantType: reg.registrantType,
      contractorName: reg.fullName,
      contractorPhone: reg.phone,
      previousCount: reg.workerCount,
      requestedCount,
      note,
      status: 'pending',
      handledBy: null,
      handledByName: null,
      handledAt: null,
      createdAt: dateToTs(at),
    });
    this.updateReg(jobId, reg.id, { pendingRequestId: id });
    const label = requestedCount === 0 ? 'ביטול הגעה' : `מ-${reg.workerCount} ל-${requestedCount} עובדים`;
    this.notify('change_request', jobId, 'בקשת שינוי חדשה', `${reg.fullName} (${REGISTRANT_TYPE_LABELS[reg.registrantType]}) — ${label}. ${job.name}`, '/requests', null, at, `change_request:${id}`);
    this.log({ uid: `contractor:${reg.id}`, displayName: reg.fullName }, 'request.create', jobId, { workerCount: reg.workerCount }, { workerCount: requestedCount }, at);
    this.recompute(jobId);
    return id;
  }

  private updateReg(jobId: string, cid: string, patch: Partial<DemoReg>) {
    this.regs.set(jobId, (this.regs.get(jobId) ?? []).map((r) => (r.id === cid ? { ...r, ...patch, updatedAt: dateToTs(new Date()) } : r)));
  }

  private removeReg(jobId: string, cid: string) {
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    if (!reg) return;
    this.regs.set(jobId, (this.regs.get(jobId) ?? []).filter((r) => r.id !== cid));
    if (reg.personalTokenHash) this.tokens.set(reg.personalTokenHash, { jobId, cid, revoked: true });
    const c = this.contractorsById.get(cid);
    if (c) {
      const jobIds = c.jobIds.filter((x) => x !== jobId);
      this.contractorsById.set(cid, { ...c, jobIds, jobCount: jobIds.length });
    }
  }

  private closeRequest(id: string, status: 'approved' | 'rejected' | 'cancelled', by: { uid: string; displayName: string }) {
    this.reqs = this.reqs.map((r) => (r.id === id ? { ...r, status, handledBy: by.uid, handledByName: by.displayName, handledAt: dateToTs(new Date()) } : r));
    this.notes = this.notes.map((n) => (n.id === `change_request:${id}` ? { ...n, resolution: status, resolvedByName: by.displayName } : n));
  }

  private emit() {
    this.persist();
    this.notifyListeners();
  }

  private notifyListeners() {
    for (const l of this.listeners) l();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── קריאה ──
  jobs(status: 'active' | 'archived'): DemoJob[] {
    this.ensureSeed();
    const list = [...this.jobsById.values()].filter((j) => j.status === status);
    return list.sort((a, b) => (status === 'active' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)));
  }
  job(id: string): DemoJob | null {
    this.ensureSeed();
    return this.jobsById.get(id) ?? null;
  }
  secret(id: string) {
    this.ensureSeed();
    return this.secrets.get(id) ?? null;
  }
  registrations(id: string): DemoReg[] {
    this.ensureSeed();
    return this.regs.get(id) ?? [];
  }
  activity(id: string): DemoLog[] {
    this.ensureSeed();
    return this.logs.filter((l) => l.jobId === id);
  }
  pendingRequests(): number {
    this.ensureSeed();
    return this.reqs.filter((r) => r.status === 'pending').length;
  }
  requests(): DemoRequest[] {
    this.ensureSeed();
    return this.reqs;
  }
  contractors(): DemoContractor[] {
    this.ensureSeed();
    return [...this.contractorsById.values()].sort((a, b) => (tsToDate(b.lastSeenAt)?.getTime() ?? 0) - (tsToDate(a.lastSeenAt)?.getTime() ?? 0));
  }
  contractor(id: string): DemoContractor | null {
    this.ensureSeed();
    return this.contractorsById.get(id) ?? null;
  }
  contractorHistory(id: string) {
    this.ensureSeed();
    const c = this.contractorsById.get(id);
    if (!c) return [];
    return c.jobIds
      .map((jobId) => ({ job: this.jobsById.get(jobId) ?? null, reg: (this.regs.get(jobId) ?? []).find((r) => r.id === id) ?? null }))
      .filter((x): x is { job: DemoJob; reg: DemoReg } => Boolean(x.job && x.reg))
      .sort((a, b) => b.job.date.localeCompare(a.job.date));
  }
  notifications(): DemoNotification[] {
    this.ensureSeed();
    return this.notes.filter((n) => n.actorUid !== DEMO_ME.uid);
  }
  unreadCount(): number {
    return this.notifications().filter((n) => !n.readBy[DEMO_ME.uid]).length;
  }

  // ── כתיבה: עבודות ──
  private mustGet(id: string): DemoJob {
    this.ensureSeed();
    const j = this.jobsById.get(id);
    if (!j) throw fail('not_found', 'העבודה לא נמצאה', 404);
    return j;
  }

  private patch(id: string, patch: Partial<DemoJob>, bumpVersion = true) {
    const j = this.mustGet(id);
    this.jobsById.set(id, { ...j, ...patch, version: bumpVersion ? j.version + 1 : j.version, updatedAt: dateToTs(new Date()) });
  }

  async createJob(body: JobWriteRequest): Promise<{ jobId: string }> {
    this.ensureSeed();
    await delay();
    const now = new Date();
    const v = validateJobInput(body, now, { isNew: true });
    if (!v.ok) throw fail('bad_request', 'יש לתקן את השדות המסומנים', 400, v.errors as Record<string, string>);
    const id = nextId('demonew').padEnd(20, '0').slice(0, 20);
    this.jobsById.set(id, {
      id,
      ...v.value,
      registrationClosesAt: v.value.registrationClosesAt ? dateToTs(v.value.registrationClosesAt) : null,
      status: 'active',
      registration: 'open',
      registrationClosedReason: null,
      registrationSlug: Math.random().toString(36).slice(2, 12).padEnd(10, 'k'),
      totalWorkers: 0,
      contractorCount: 0,
      individualWorkerCount: 0,
      pendingRequestCount: 0,
      archivedAt: null,
      archiveHoldUntil: null,
      createdBy: DEMO_ME.uid,
      updatedBy: DEMO_ME.uid,
      createdAt: dateToTs(now),
      updatedAt: dateToTs(now),
      version: 1,
    });
    this.secrets.set(id, { accessCode: String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0'), rotatedAt: dateToTs(now) });
    this.regs.set(id, []);
    this.log(DEMO_ME, 'job.create', id, null, { name: v.value.name, date: v.value.date, reportTime: v.value.reportTime, location: v.value.location });
    this.emit();
    return { jobId: id };
  }

  async updateJob(id: string, body: JobUpdateRequest): Promise<{ changed: string[] }> {
    await delay();
    const j = this.mustGet(id);
    if (j.version !== body.version) throw fail('conflict', 'מנהל אחר עדכן את העבודה בינתיים', 409);
    const v = validateJobInput(body, new Date(), { isNew: false });
    if (!v.ok) throw fail('bad_request', 'יש לתקן את השדות המסומנים', 400, v.errors as Record<string, string>);
    const before: Record<string, unknown> = {};
    const after: Record<string, unknown> = {};
    for (const k of ['name', 'date', 'reportTime', 'location', 'notes'] as const) {
      if (j[k] !== v.value[k]) {
        before[k] = j[k];
        after[k] = v.value[k];
      }
    }
    const oldClose = tsToDate(j.registrationClosesAt)?.getTime() ?? null;
    const newClose = v.value.registrationClosesAt?.getTime() ?? null;
    if (oldClose !== newClose) {
      before.registrationClosesAt = oldClose ? new Date(oldClose).toISOString() : null;
      after.registrationClosesAt = newClose ? new Date(newClose).toISOString() : null;
    }
    const changed = Object.keys(after);
    if (!changed.length) return { changed };
    this.patch(id, { ...v.value, registrationClosesAt: v.value.registrationClosesAt ? dateToTs(v.value.registrationClosesAt) : null });
    this.log(DEMO_ME, 'job.update', id, before, after);
    this.emit();
    return { changed };
  }

  async setRegistration(id: string, action: RegistrationAction) {
    await delay();
    const j = this.mustGet(id);
    if (j.status === 'archived') throw fail('conflict', 'העבודה בארכיון. יש להחזיר אותה לפעילות קודם', 409);
    const target = action === 'open' ? 'open' : 'closed';
    if (j.registration !== target) {
      this.patch(id, { registration: target, registrationClosedReason: target === 'closed' ? 'manual' : null });
      this.log(DEMO_ME, target === 'open' ? 'job.registration.open' : 'job.registration.close', id, { registration: j.registration }, { registration: target });
      this.emit();
    }
    return { ok: true as const };
  }

  async archive(id: string) {
    await delay();
    const j = this.mustGet(id);
    if (j.status !== 'archived') {
      this.patch(id, { status: 'archived', archivedAt: dateToTs(new Date()), archiveHoldUntil: null });
      this.setThreadsClosed(id, true);
      this.log(DEMO_ME, 'job.archive', id, { status: 'active' }, { status: 'archived' });
      this.emit();
    }
    return { ok: true as const };
  }

  async unarchive(id: string) {
    await delay();
    const j = this.mustGet(id);
    if (j.status !== 'active') {
      const hold = archiveHoldAfterRestore(new Date());
      this.patch(id, { status: 'active', archivedAt: null, archiveHoldUntil: dateToTs(hold) });
      this.setThreadsClosed(id, false);
      this.log(DEMO_ME, 'job.unarchive', id, { status: 'archived' }, { status: 'active', archiveHoldUntil: hold.toISOString() });
      this.emit();
    }
    return { ok: true as const };
  }

  async rotate(id: string, target: AccessRotateTarget) {
    await delay();
    this.mustGet(id);
    if (target === 'code') {
      this.secrets.set(id, { accessCode: String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0'), rotatedAt: dateToTs(new Date()) });
    } else {
      this.patch(id, { registrationSlug: Math.random().toString(36).slice(2, 12).padEnd(10, 'k') });
    }
    this.log(DEMO_ME, 'job.access.rotate', id, null, { rotated: target === 'code' ? 'קוד כניסה' : 'קישור הרשמה' });
    this.emit();
    return { ok: true as const };
  }

  async remove(id: string) {
    await delay();
    const j = this.mustGet(id);
    for (const r of this.regs.get(id) ?? []) this.removeReg(id, r.id);
    this.reqs = this.reqs.filter((r) => r.jobId !== id);
    this.anns.delete(id);
    for (const t of [...this.threadsById.values()].filter((x) => x.jobId === id)) {
      this.threadsById.delete(t.id);
      this.msgs.delete(t.id);
    }
    this.jobsById.delete(id);
    this.secrets.delete(id);
    this.regs.delete(id);
    this.log(DEMO_ME, 'job.delete', id, { name: j.name, date: j.date }, null);
    this.emit();
    return { ok: true as const };
  }

  // ── כתיבה: נרשמים (מנהל) ──
  async addRegistration(jobId: string, body: AddRegistrationRequest): Promise<{ personalToken: string }> {
    await delay();
    const job = this.mustGet(jobId);
    if (job.status !== 'active') throw fail('conflict', 'העבודה בארכיון. יש להחזיר אותה לפעילות קודם', 409);
    let fullName = body.fullName ?? '';
    let phone = body.phone ?? '';
    if (body.contractorId) {
      const c = this.contractorsById.get(body.contractorId);
      if (!c) throw fail('not_found', 'הקבלן לא נמצא במאגר', 404);
      fullName = c.fullName;
      phone = c.phone;
    }
    const v = validateRegistration({ registrantType: body.registrantType, fullName, phone, ...(typeof body.workerCount === 'number' ? { workerCount: body.workerCount } : {}) });
    if (!v.ok) throw fail('bad_request', 'יש לתקן את השדות המסומנים', 400, v.errors as Record<string, string>);
    const { token } = this.insertReg(jobId, v.value, 'admin', new Date());
    this.emit();
    return { personalToken: token };
  }

  async updateCount(jobId: string, cid: string, workerCount: number) {
    await delay();
    this.mustGet(jobId);
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    if (!reg) throw fail('not_found', 'הרישום לא נמצא', 404);
    if (reg.registrantType === 'worker') throw fail('bad_request', 'עובד עצמאי נספר תמיד כעובד אחד');
    if (!Number.isInteger(workerCount) || workerCount < 1 || workerCount > 500) throw fail('bad_request', 'הכמות צריכה להיות בין 1 ל-500');
    if (reg.pendingRequestId) this.closeRequest(reg.pendingRequestId, 'cancelled', DEMO_ME);
    this.updateReg(jobId, cid, { workerCount, pendingRequestId: null });
    this.log(DEMO_ME, 'registration.updateCount', jobId, { fullName: reg.fullName, workerCount: reg.workerCount }, { fullName: reg.fullName, workerCount });
    this.recompute(jobId);
    this.emit();
    return { ok: true as const };
  }

  async removeRegistration(jobId: string, cid: string) {
    await delay();
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    if (!reg) throw fail('not_found', 'הרישום לא נמצא', 404);
    if (reg.pendingRequestId) this.closeRequest(reg.pendingRequestId, 'cancelled', DEMO_ME);
    this.removeReg(jobId, cid);
    this.log(DEMO_ME, 'registration.remove', jobId, { fullName: reg.fullName, registrantType: reg.registrantType, workerCount: reg.workerCount }, null);
    this.recompute(jobId);
    this.emit();
    return { ok: true as const };
  }

  async tokenAction(jobId: string, cid: string, action: TokenAction): Promise<{ personalToken?: string; ok?: true }> {
    await delay();
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    if (!reg) throw fail('not_found', 'הרישום לא נמצא', 404);
    if (reg.personalTokenHash) this.tokens.set(reg.personalTokenHash, { jobId, cid, revoked: true });
    let token: string | null = null;
    if (action === 'reissue') {
      token = randomToken();
      this.tokens.set(token, { jobId, cid, revoked: false });
    }
    this.updateReg(jobId, cid, { personalTokenHash: token });
    this.log(DEMO_ME, action === 'reissue' ? 'registration.token.reissue' : 'registration.token.revoke', jobId, null, { fullName: reg.fullName });
    this.emit();
    return token ? { personalToken: token } : { ok: true };
  }

  async decide(requestId: string, decision: RequestDecision) {
    await delay();
    const req = this.reqs.find((r) => r.id === requestId);
    if (!req) throw fail('not_found', 'הבקשה לא נמצאה', 404);
    if (req.status !== 'pending') {
      const what = req.status === 'approved' ? 'אושרה' : req.status === 'rejected' ? 'נדחתה' : 'בוטלה';
      throw fail('already_handled', `הבקשה כבר ${what} על ידי ${req.handledByName ?? 'מנהל אחר'}`, 409);
    }
    const reg = (this.regs.get(req.jobId) ?? []).find((r) => r.id === req.contractorId);
    if (!reg) {
      this.closeRequest(requestId, 'cancelled', DEMO_ME);
      this.emit();
      return { status: 'cancelled' as const };
    }
    if (decision === 'reject') {
      this.closeRequest(requestId, 'rejected', DEMO_ME);
      this.updateReg(req.jobId, reg.id, { pendingRequestId: null });
    } else {
      this.closeRequest(requestId, 'approved', DEMO_ME);
      if (req.requestedCount === 0) this.removeReg(req.jobId, reg.id);
      else this.updateReg(req.jobId, reg.id, { workerCount: req.requestedCount, pendingRequestId: null });
    }
    this.log(DEMO_ME, decision === 'approve' ? 'request.approve' : 'request.reject', req.jobId, { fullName: reg.fullName, workerCount: reg.workerCount }, { fullName: reg.fullName, workerCount: req.requestedCount });
    this.recompute(req.jobId);
    this.emit();
    return { status: decision === 'approve' ? ('approved' as const) : ('rejected' as const) };
  }

  private resolveCid(phoneE164: string): string {
    return this.phoneIds.get(phoneE164) ?? cidOf(phoneE164);
  }

  async updateContractor(cid: string, body: UpdateContractorRequest) {
    await delay();
    const c = this.contractorsById.get(cid);
    if (!c) throw fail('not_found', 'הקבלן לא נמצא', 404);
    let name = c.fullName;
    if (body.fullName !== undefined) {
      name = body.fullName.trim().replace(/\s+/g, ' ');
      if (name.length < 2) throw fail('bad_request', 'יש להזין שם מלא', 400, { fullName: 'יש להזין שם מלא' });
    }
    let phone = c.phone;
    if (body.phone !== undefined) {
      const p = parseIsraeliPhone(body.phone);
      if (!p.ok) throw fail('bad_request', PHONE_ERROR_MESSAGES[p.reason], 400, { phone: PHONE_ERROR_MESSAGES[p.reason] });
      phone = p.e164;
    }
    if (name === c.fullName && phone === c.phone) return { ok: true as const };
    if (phone !== c.phone) {
      const owner = this.resolveCid(phone);
      const other = owner !== cid ? this.contractorsById.get(owner) : undefined;
      if (other) {
        const msg = `המספר הזה כבר שייך ל${other.fullName} במאגר`;
        throw fail('conflict', msg, 409, { phone: msg });
      }
      this.phoneIds.set(phone, cid);
      this.phoneIds.set(c.phone, `d${Date.now()}${Math.floor(Math.random() * 100)}`);
    }
    const before: Record<string, unknown> = {};
    const after: Record<string, unknown> = {};
    if (name !== c.fullName) {
      before.fullName = c.fullName;
      after.fullName = name;
    }
    if (phone !== c.phone) {
      before.phone = formatPhoneForDisplay(c.phone);
      after.phone = formatPhoneForDisplay(phone);
    }
    this.contractorsById.set(cid, { ...c, fullName: name, phone });
    for (const jobId of c.jobIds) {
      if (this.jobsById.get(jobId)?.status !== 'active') continue;
      this.updateReg(jobId, cid, { fullName: name, phone });
      const t = this.threadsById.get(threadIdFor(jobId, cid));
      if (t) this.threadsById.set(t.id, { ...t, contractorName: name });
      this.log(DEMO_ME, 'contractor.update', jobId, before, after);
    }
    this.emit();
    return { ok: true as const };
  }

  async markRead(ids: string[] | 'all') {
    const now = dateToTs(new Date());
    this.notes = this.notes.map((n) => (ids === 'all' || ids.includes(n.id) ? { ...n, readBy: { ...n.readBy, [DEMO_ME.uid]: now } } : n));
    this.emit();
    return { ok: true as const };
  }

  // ── צד הקבלן (נקודות ציבוריות) ──
  private publicJob(j: DemoJob): PublicJobInfo {
    const closes = tsToDate(j.registrationClosesAt);
    return {
      name: j.name,
      date: j.date,
      reportTime: j.reportTime,
      location: j.location,
      notes: j.notes,
      active: j.status === 'active',
      registrationOpen: j.status === 'active' && j.registration === 'open' && !(closes && closes.getTime() <= Date.now()),
    };
  }

  private bySlug(slug: string): DemoJob {
    this.ensureSeed();
    const j = [...this.jobsById.values()].find((x) => x.registrationSlug === slug);
    if (!j) throw fail('not_found', 'הקישור אינו תקין או שהוחלף. בקשו מהמנהל קישור עדכני', 404);
    return j;
  }

  async enter(slug: string, code: string): Promise<PublicJobInfo> {
    await delay();
    const j = this.bySlug(slug);
    if (this.secrets.get(j.id)?.accessCode !== code) throw fail('forbidden', 'קוד שגוי. נותרו 4 ניסיונות', 403);
    const announcements = this.announcements(j.id)
      .filter((a) => a.audience === 'all' && a.showOnRegistration)
      .map((a) => this.publicAnn(a));
    const st = this.settings();
    const info: PublicJobInfo = { ...this.publicJob(j), maxWorkers: st.maxWorkersPerContractor, announcements };
    if (st.publicPaused) return { ...info, registrationOpen: false, notice: st.publicPausedMessage };
    if (st.registrationNotice) info.notice = st.registrationNotice;
    return info;
  }

  async register(slug: string, body: RegisterRequest): Promise<{ personalToken: string }> {
    await delay();
    const j = this.bySlug(slug);
    if (this.secrets.get(j.id)?.accessCode !== body.code) throw fail('forbidden', 'קוד שגוי', 403);
    const st = this.settings();
    if (st.publicPaused) throw fail('conflict', st.publicPausedMessage, 409);
    const v = validateRegistration({ registrantType: body.registrantType, fullName: body.fullName, phone: body.phone, ...(typeof body.workerCount === 'number' ? { workerCount: body.workerCount } : {}) }, st.maxWorkersPerContractor);
    if (!v.ok) throw fail('bad_request', 'יש לתקן את השדות המסומנים', 400, v.errors as Record<string, string>);
    if (!this.publicJob(j).registrationOpen) throw fail('conflict', 'ההרשמה לעבודה הזו סגורה', 409);
    const { token } = this.insertReg(j.id, v.value, 'self', new Date());
    this.emit();
    return { personalToken: token };
  }

  private byToken(token: string) {
    this.ensureSeed();
    const t = this.tokens.get(token);
    if (!t || t.revoked) throw fail('not_found', 'הקישור האישי אינו בתוקף. בקשו מהמנהל קישור חדש', 404);
    const job = this.jobsById.get(t.jobId);
    const reg = (this.regs.get(t.jobId) ?? []).find((r) => r.id === t.cid);
    if (!job || !reg) throw fail('not_found', 'הרישום הזה הוסר. לפרטים פנו למנהל', 404);
    return { job, reg };
  }

  async personal(token: string): Promise<PersonalViewResponse> {
    await delay();
    const { job, reg } = this.byToken(token);
    const mine = this.reqs
      .filter((r) => r.jobId === job.id && r.contractorId === reg.id)
      .map(
        (r): PersonalRequestView => ({
          id: r.id,
          previousCount: r.previousCount,
          requestedCount: r.requestedCount,
          note: r.note,
          status: r.status,
          createdAt: tsToDate(r.createdAt)!.toISOString(),
          handledAt: tsToDate(r.handledAt)?.toISOString() ?? null,
        }),
      );
    const st = this.settings();
    const notice = st.publicPaused ? st.publicPausedMessage : st.personalPageNotice;
    return {
      job: { ...this.publicJob(job), maxWorkers: st.maxWorkersPerContractor, ...(notice ? { notice } : {}) },
      registration: { registrantType: reg.registrantType, fullName: reg.fullName, phoneMasked: maskPhone(reg.phone), workerCount: reg.workerCount },
      pendingRequest: mine.find((r) => r.status === 'pending') ?? null,
      history: mine.filter((r) => r.status !== 'pending'),
      announcements: this.announcements(job.id)
        .filter((a) => announcementVisibleTo(a, reg))
        .map((a) => ({ ...this.publicAnn(a), isNew: tsToDate(a.updatedAt)!.getTime() > (tsToDate(reg.announcementsSeenAt)?.getTime() ?? 0) })),
      chat: (() => {
        const t = this.threadsById.get(threadIdFor(job.id, reg.id));
        const unread = t && t.lastSender === 'admin' && tsToDate(t.lastMessageAt)!.getTime() > (tsToDate(t.contractorReadAt)?.getTime() ?? 0) ? 1 : 0;
        return { unread, closed: job.status !== 'active' };
      })(),
    };
  }

  async createRequest(token: string, requestedCount: number, note: string) {
    await delay();
    const { job, reg } = this.byToken(token);
    if (job.status !== 'active') throw fail('conflict', 'העבודה הסתיימה ולא ניתן לשנות את הרישום', 409);
    if (reg.pendingRequestId) throw fail('conflict', 'כבר יש בקשה שממתינה לאישור', 409);
    const st = this.settings();
    if (st.publicPaused) throw fail('conflict', st.publicPausedMessage, 409);
    const v = validateChangeRequest(reg.registrantType, reg.workerCount, requestedCount, note, st.maxWorkersPerContractor);
    if (!v.ok) throw fail('bad_request', v.error);
    const id = this.insertRequest(job.id, reg, v.value.requestedCount, v.value.note);
    this.emit();
    return { requestId: id };
  }

  async cancelRequest(token: string) {
    await delay();
    const { job, reg } = this.byToken(token);
    if (!reg.pendingRequestId) throw fail('conflict', 'אין בקשה ממתינה', 409);
    this.closeRequest(reg.pendingRequestId, 'cancelled', { uid: `contractor:${reg.id}`, displayName: reg.fullName });
    this.updateReg(job.id, reg.id, { pendingRequestId: null });
    this.recompute(job.id);
    this.emit();
    return { ok: true as const };
  }
  private setThreadsClosed(jobId: string, closed: boolean) {
    for (const t of this.threadsById.values()) if (t.jobId === jobId) this.threadsById.set(t.id, { ...t, closed });
  }

  // ── עדכונים לנרשמים ──
  private publicAnn(a: DemoAnnouncement): PublicAnnouncement {
    return { id: a.id, text: a.text, createdAt: tsToDate(a.createdAt)!.toISOString(), updatedAt: tsToDate(a.updatedAt)!.toISOString() };
  }

  announcements(jobId: string): DemoAnnouncement[] {
    this.ensureSeed();
    return [...(this.anns.get(jobId) ?? [])].sort((a, b) => tsToDate(b.createdAt)!.getTime() - tsToDate(a.createdAt)!.getTime());
  }

  async createAnnouncement(jobId: string, body: AnnouncementRequest) {
    await delay();
    const job = this.mustGet(jobId);
    if (job.status !== 'active') throw fail('conflict', 'העבודה בארכיון — אי אפשר לפרסם עדכונים', 409);
    const v = validateAnnouncement(body);
    if (!v.ok) throw fail('bad_request', v.error);
    const now = dateToTs(new Date());
    const a: DemoAnnouncement = { id: nextId('ann'), ...v.value, createdBy: DEMO_ME.uid, createdByName: DEMO_ME.displayName, createdAt: now, updatedAt: now };
    this.anns.set(jobId, [...(this.anns.get(jobId) ?? []), a]);
    this.log(DEMO_ME, 'announcement.create', jobId, null, { text: v.value.text.slice(0, 120) });
    this.emit();
    return { id: a.id };
  }

  async updateAnnouncement(jobId: string, id: string, body: AnnouncementRequest) {
    await delay();
    const v = validateAnnouncement(body);
    if (!v.ok) throw fail('bad_request', v.error);
    const list = this.anns.get(jobId) ?? [];
    const old = list.find((a) => a.id === id);
    if (!old) throw fail('not_found', 'העדכון לא נמצא', 404);
    this.anns.set(jobId, list.map((a) => (a.id === id ? { ...a, ...v.value, updatedAt: dateToTs(new Date()) } : a)));
    this.log(DEMO_ME, 'announcement.update', jobId, { text: old.text.slice(0, 120) }, { text: v.value.text.slice(0, 120) });
    this.emit();
    return { ok: true as const };
  }

  async deleteAnnouncement(jobId: string, id: string) {
    await delay();
    const old = (this.anns.get(jobId) ?? []).find((a) => a.id === id);
    this.anns.set(jobId, (this.anns.get(jobId) ?? []).filter((a) => a.id !== id));
    if (old) this.log(DEMO_ME, 'announcement.delete', jobId, { text: old.text.slice(0, 120) }, null);
    this.emit();
    return { ok: true as const };
  }

  async markSeen(token: string) {
    const { job, reg } = this.byToken(token);
    this.updateReg(job.id, reg.id, { announcementsSeenAt: dateToTs(new Date()) });
    this.emit();
    return { ok: true as const };
  }

  // ── שיחות עם נרשמים ──
  threads(): DemoThread[] {
    this.ensureSeed();
    return [...this.threadsById.values()].sort((a, b) => tsToDate(b.lastMessageAt)!.getTime() - tsToDate(a.lastMessageAt)!.getTime());
  }

  thread(id: string): DemoThread | null {
    this.ensureSeed();
    return this.threadsById.get(id) ?? null;
  }

  threadMessages(id: string): DemoMessage[] {
    this.ensureSeed();
    return this.msgs.get(id) ?? [];
  }

  private appendMessage(jobId: string, reg: DemoReg, m: Omit<DemoMessage, 'id' | 'createdAt'>) {
    const job = this.jobsById.get(jobId)!;
    const tid = threadIdFor(jobId, reg.id);
    const now = new Date();
    this.msgs.set(tid, [...(this.msgs.get(tid) ?? []), { ...m, id: nextId('msg'), createdAt: dateToTs(now) }]);
    const prev = this.threadsById.get(tid);
    this.threadsById.set(tid, {
      id: tid,
      jobId,
      jobName: job.name,
      jobDate: job.date,
      contractorId: reg.id,
      contractorName: reg.fullName,
      registrantType: reg.registrantType,
      lastMessageAt: dateToTs(now),
      lastMessageText: m.text.slice(0, 140),
      lastSender: m.sender,
      readBy: m.sender === 'admin' ? { ...(prev?.readBy ?? {}), [DEMO_ME.uid]: dateToTs(now) } : (prev?.readBy ?? {}),
      contractorReadAt: m.sender === 'contractor' ? dateToTs(now) : (prev?.contractorReadAt ?? null),
      closed: false,
    });
    if (m.sender === 'contractor') {
      this.notify('message', jobId, `הודעה מ${reg.fullName}`, `${m.text.slice(0, 120)} — ${job.name}`, `/messages/${tid}`, null, now, `message:${tid}`);
    }
    return tid;
  }

  async adminSend(threadId: string, text: string) {
    await delay();
    const [jobId, cid] = threadId.split('__') as [string, string];
    const job = this.mustGet(jobId);
    if (job.status !== 'active') throw fail('conflict', 'העבודה בארכיון — השיחה סגורה לכתיבה', 409);
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    if (!reg) throw fail('conflict', 'הנרשם הוסר מהעבודה — אי אפשר לשלוח לו הודעות', 409);
    const v = validateMessageText(text);
    if (!v.ok) throw fail('bad_request', v.error);
    this.appendMessage(jobId, reg, { sender: 'admin', authorId: DEMO_ME.uid, authorName: DEMO_ME.displayName, text: v.value });
    this.emit();
    return { ok: true as const };
  }

  async adminRead(threadId: string) {
    const t = this.threadsById.get(threadId);
    if (!t) return { ok: true as const };
    const now = dateToTs(new Date());
    this.threadsById.set(threadId, { ...t, readBy: { ...t.readBy, [DEMO_ME.uid]: now } });
    this.notes = this.notes.map((n) => (n.id === `message:${threadId}` ? { ...n, readBy: { ...n.readBy, [DEMO_ME.uid]: now } } : n));
    this.emit();
    return { ok: true as const };
  }

  /** מוכן לשיחה חדשה עם נרשם (לפני ההודעה הראשונה) */
  regForThread(threadId: string): { job: DemoJob; reg: DemoReg } | null {
    this.ensureSeed();
    const [jobId, cid] = threadId.split('__') as [string, string];
    const job = this.jobsById.get(jobId);
    const reg = (this.regs.get(jobId) ?? []).find((r) => r.id === cid);
    return job && reg ? { job, reg } : null;
  }

  // ── צד הנרשם ──
  async contractorChat(token: string): Promise<ChatResponse> {
    const { job, reg } = this.byToken(token);
    const list = this.msgs.get(threadIdFor(job.id, reg.id)) ?? [];
    return {
      closed: job.status !== 'active',
      messages: list.map((m) => ({ id: m.id, sender: m.sender, authorName: m.authorName, text: m.text, createdAt: tsToDate(m.createdAt)!.toISOString() })),
    };
  }

  async contractorSend(token: string, text: string) {
    await delay();
    const { job, reg } = this.byToken(token);
    if (job.status !== 'active') throw fail('conflict', 'העבודה הסתיימה — השיחה סגורה לכתיבה', 409);
    const v = validateMessageText(text);
    if (!v.ok) throw fail('bad_request', v.error);
    this.appendMessage(job.id, reg, { sender: 'contractor', authorId: `contractor:${reg.id}`, authorName: reg.fullName, text: v.value });
    this.emit();
    return { ok: true as const };
  }

  async contractorRead(token: string) {
    const { job, reg } = this.byToken(token);
    const tid = threadIdFor(job.id, reg.id);
    const t = this.threadsById.get(tid);
    if (t) {
      this.threadsById.set(tid, { ...t, contractorReadAt: dateToTs(new Date()) });
      this.emit();
    }
    return { ok: true as const };
  }

  // ── צ'אט המנהלים ──
  teamMessages(): DemoTeamMessage[] {
    this.ensureSeed();
    return this.team;
  }

  teamLastRead(): Ts | null {
    this.ensureSeed();
    return this.teamReadAt;
  }

  async teamSend(text: string) {
    await delay();
    const v = validateMessageText(text);
    if (!v.ok) throw fail('bad_request', v.error);
    const now = dateToTs(new Date());
    this.team = [...this.team, { id: nextId('team'), authorUid: DEMO_ME.uid, authorName: DEMO_ME.displayName, text: v.value, createdAt: now }];
    this.teamReadAt = now;
    this.emit();
    return { ok: true as const };
  }

  async teamRead() {
    this.teamReadAt = dateToTs(new Date());
    this.notes = this.notes.map((n) => (n.type === 'team_message' ? { ...n, readBy: { ...n.readBy, [DEMO_ME.uid]: this.teamReadAt! } } : n));
    this.emit();
    return { ok: true as const };
  }

  // ── מסך המפתח (מדמה את /api/owner/*) ──
  settings(): SystemSettings {
    this.ensureSeed();
    return resolveSettings(this.settingsDoc);
  }

  /** הגדרות גולמיות — כמו קריאת v2_config/settings מ-Firestore */
  settingsRaw(): SettingsDoc<Ts> {
    this.ensureSeed();
    return this.settingsDoc;
  }

  private ensureAdmins() {
    this.ensureSeed();
    if (this.admins.length) return;
    const at = new Date(Date.now() - 20 * 86_400_000).toISOString();
    this.admins = [
      { uid: DEMO_ME.uid, email: DEMO_CREDENTIALS.email, displayName: DEMO_ME.displayName, active: true, removed: false, hasPassword: true, createdAt: at },
      { uid: OTHER_ADMINS[0]!.uid, email: 'dana@example.com', displayName: OTHER_ADMINS[0]!.displayName, active: true, removed: false, hasPassword: true, createdAt: at },
      { uid: OTHER_ADMINS[1]!.uid, email: 'yossi@example.com', displayName: OTHER_ADMINS[1]!.displayName, active: true, removed: false, hasPassword: false, createdAt: at },
    ];
  }

  async ownerAdmins(): Promise<AdminsResponse> {
    await delay();
    this.ensureAdmins();
    const admins: AdminListItem[] = this.admins
      .filter((a) => !a.removed)
      .map((a) => ({
        uid: a.uid,
        email: a.email,
        displayName: a.displayName,
        role: a.uid === DEMO_ME.uid ? 'owner' : 'admin',
        active: a.active,
        hasPassword: a.hasPassword,
        lastLoginAt: a.uid === DEMO_ME.uid ? new Date().toISOString() : a.hasPassword ? new Date(Date.now() - 3 * 3_600_000).toISOString() : null,
        pushDevices: a.uid === DEMO_ME.uid ? 1 : a.hasPassword ? 2 : 0,
        createdAt: a.createdAt,
      }));
    return { admins, max: this.settings().maxAdmins };
  }

  private demoLink(email: string) {
    return `https://example.firebaseapp.com/__/auth/action?mode=resetPassword&oobCode=DEMO&email=${encodeURIComponent(email)}`;
  }

  async ownerAddAdmin(body: AddAdminRequest): Promise<AddAdminResponse> {
    await delay();
    this.ensureAdmins();
    const email = (body.email ?? '').trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw fail('bad_request', 'כתובת האימייל לא תקינה', 400, { email: 'כתובת האימייל לא תקינה' });
    const name = (body.displayName ?? '').trim().replace(/\s+/g, ' ');
    if (name.length < 2) throw fail('bad_request', 'יש להזין שם מלא', 400, { displayName: 'יש להזין שם מלא' });
    const maxAdmins = this.settings().maxAdmins;
    if (this.admins.filter((a) => a.active && !a.removed).length >= maxAdmins) {
      throw fail('conflict', `כבר יש ${maxAdmins} מנהלים פעילים — המקסימום שנקבע בהגדרות המערכת. אפשר להגדיל אותו, או להשבית/להסיר מנהל`, 409);
    }
    const existing = this.admins.find((a) => a.email === email);
    if (existing && existing.active && !existing.removed) throw fail('conflict', `${existing.displayName} כבר מנהל פעיל`, 409);
    const uid = existing?.uid ?? nextId('adm');
    const next: DemoAdmin = { uid, email, displayName: name, active: true, removed: false, hasPassword: existing?.hasPassword ?? false, createdAt: existing?.createdAt ?? new Date().toISOString() };
    this.admins = [...this.admins.filter((a) => a.uid !== uid), next];
    this.log(DEMO_ME, 'admin.add', null, null, { fullName: name, email });
    this.emit();
    return { uid, existingUser: !!existing, passwordLink: next.hasPassword ? null : this.demoLink(email) };
  }

  async ownerAdminAction(uid: string, body: AdminActionRequest): Promise<{ ok: true } | { passwordLink: string }> {
    await delay();
    this.ensureAdmins();
    const a = this.admins.find((x) => x.uid === uid && !x.removed);
    if (!a) throw fail('not_found', 'המנהל לא נמצא', 404);
    if (uid === DEMO_ME.uid && (body.action === 'disable' || body.action === 'remove')) throw fail('bad_request', 'אי אפשר להשבית או להסיר את המפתח');
    const set = (patch: Partial<DemoAdmin>) => (this.admins = this.admins.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));
    switch (body.action) {
      case 'password_link':
        return { passwordLink: this.demoLink(a.email) };
      case 'disable':
        set({ active: false });
        this.log(DEMO_ME, 'admin.disable', null, { fullName: a.displayName, active: 'כן' }, { active: 'לא' });
        break;
      case 'enable':
        if (this.admins.filter((x) => x.active && !x.removed).length >= this.settings().maxAdmins) throw fail('conflict', `כבר יש ${this.settings().maxAdmins} מנהלים פעילים — המקסימום שנקבע בהגדרות המערכת`, 409);
        set({ active: true });
        this.log(DEMO_ME, 'admin.enable', null, { fullName: a.displayName, active: 'לא' }, { active: 'כן' });
        break;
      case 'remove':
        set({ active: false, removed: true });
        this.log(DEMO_ME, 'admin.remove', null, { fullName: a.displayName, email: a.email }, {});
        break;
      case 'rename': {
        const name = (body.displayName ?? '').trim().replace(/\s+/g, ' ');
        if (name.length < 2) throw fail('bad_request', 'יש להזין שם מלא', 400, { displayName: 'יש להזין שם מלא' });
        set({ displayName: name });
        this.log(DEMO_ME, 'admin.rename', null, { fullName: a.displayName }, { fullName: name });
        break;
      }
      default:
        throw fail('bad_request', 'פעולה לא מוכרת');
    }
    this.emit();
    return { ok: true as const };
  }

  async ownerSettings(): Promise<SettingsResponse> {
    await delay();
    return {
      settings: this.settings(),
      updatedAt: tsToDate(this.settingsDoc.updatedAt)?.toISOString() ?? null,
      updatedByName: this.settingsDoc.updatedByName ?? null,
    };
  }

  async ownerUpdateSettings(patch: Partial<SystemSettings>): Promise<SettingsResponse> {
    await delay();
    const v = validateSettingsPatch(patch as Record<string, unknown>);
    if (!v.ok) throw fail('bad_request', 'יש לתקן את השדות המסומנים', 400, v.errors as Record<string, string>);
    if (v.value.maxAdmins !== undefined) {
      this.ensureAdmins();
      const active = this.admins.filter((a) => a.active && !a.removed).length;
      if (v.value.maxAdmins < active) {
        const msg = `יש כרגע ${active} מנהלים פעילים. כדי להוריד ל-${v.value.maxAdmins}, השביתו קודם מנהלים`;
        throw fail('bad_request', msg, 400, { maxAdmins: msg });
      }
    }
    const before = this.settings();
    const changed = (Object.keys(v.value) as Array<keyof SystemSettings>).filter((k) => before[k] !== v.value[k]);
    if (changed.length) {
      this.settingsDoc = { ...this.settingsDoc, ...v.value, updatedAt: dateToTs(new Date()), updatedByName: DEMO_ME.displayName };
      this.log(
        DEMO_ME,
        'settings.update',
        null,
        Object.fromEntries(changed.map((k) => [k, before[k]])),
        Object.fromEntries(changed.map((k) => [k, v.value[k]])),
      );
      this.emit();
    }
    return this.ownerSettings();
  }

  async ownerHealth(): Promise<OwnerHealthResponse> {
    await delay();
    this.ensureAdmins();
    const dayAgo = Date.now() - 86_400_000;
    return {
      version: 'הדגמה',
      serverTime: new Date().toISOString(),
      checks: [
        { key: 'worker', label: 'שרת (Cloudflare Worker)', ok: null, detail: 'מצב תצוגה — אין שרת אמיתי' },
        { key: 'firestore', label: 'מסד הנתונים (Firestore)', ok: null, detail: 'מצב תצוגה — הנתונים בדפדפן בלבד' },
        { key: 'auth', label: 'התחברות (Firebase Authentication)', ok: null, detail: 'מצב תצוגה' },
        { key: 'cron', label: 'הפעלה מתוזמנת (סגירת הרשמה וארכוב)', ok: null, detail: 'במערכת האמיתית: רצה כל 15 דקות' },
        { key: 'push', label: 'התראות Push', ok: null, detail: 'מצב תצוגה — לא נשלחות התראות' },
      ],
      counts: {
        activeJobs: this.jobs('active').length,
        archivedJobs: this.jobs('archived').length,
        contractors: this.contractors().length,
        activeAdmins: this.admins.filter((a) => a.active && !a.removed).length,
        errorsLast24h: this.errors.filter((e) => new Date(e.createdAt).getTime() > dayAgo).length,
      },
    };
  }

  /** דיווח תקלה מהדפדפן (במצב תצוגה נשמר מקומית) */
  reportError(body: Partial<ErrorReportRequest>) {
    const clean = sanitizeErrorReport(body, typeof navigator !== 'undefined' ? navigator.userAgent : '');
    this.errors = [{ id: nextId('err'), ...clean, uid: clean.source === 'admin' ? DEMO_ME.uid : null, createdAt: new Date().toISOString() }, ...this.errors].slice(0, 200);
    this.emit();
  }

  async ownerErrors(): Promise<{ errors: ErrorLogItem[] }> {
    await delay();
    this.ensureSeed();
    return { errors: this.errors };
  }

  async ownerClearErrors(): Promise<{ deleted: number }> {
    await delay();
    const n = this.errors.length;
    this.errors = [];
    this.emit();
    return { deleted: n };
  }

  quietMode(): boolean {
    this.ensureSeed();
    return this.quiet;
  }

  async ownerSetQuiet(quiet: boolean) {
    await delay();
    this.quiet = quiet;
    this.emit();
    return { ok: true as const, quietMode: quiet };
  }
}

export const demoStore = new DemoStore();
